// The other spelling of the same text: the code points, which is what somebody has when they have a bug report, a
// hex dump or an escape and not the character it stands for. The two modes are one box read two ways, so switching
// between them is a rewrite of what is in it rather than a second field with a second state to keep in step.
export interface Reading {
  text: string;
  error: string;
}

// Whatever a code point was written as: `U+0041`, `0041`, `0x41`, `A`, `\u{41}`, `&#x41;` or `&#65;`. A run of
// them is separated by anything that is not one, which is what lets a line pasted out of a dump be read as it is.
export function readPoints(value: string): Reading {
  const codes: number[] = [];
  for (const token of value.split(SEPARATORS)) {
    if (token === "") continue;
    const code = readPoint(token);
    if (code === undefined) return { text: "", error: `${token} is not a code point` };
    if (code > 0x10FFFF) return { text: "", error: `${token} is past U+10FFFF, which is the last one there is` };
    codes.push(code);
  }
  return { text: String.fromCodePoint(...codes), error: "" };
}

export function writePoints(text: string): string {
  return [...text].map((character) =>
    `U+${(character.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, "0")}`
  )
    .join(" ");
}

const SEPARATORS = /[\s,]+/;

// Hex unless it is the one decimal spelling anybody writes, which is the HTML reference without its `x`: `&#233;`
// is 233 and `&#x233;` is 563, and reading either as the other is a character nobody asked about.
function readPoint(token: string): number | undefined {
  const decimal = /^&#(\d+);?$/.exec(token);
  if (decimal) return Number(decimal[1]);

  const hex = /^(?:u\+|0x|\\u\{|\\u|\\x|&#x)?([0-9a-f]{1,6})[};]*$/i.exec(token);
  return hex ? Number.parseInt(hex[1], 16) : undefined;
}
